import { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { apiGetAllCategories, apiDeleteCategory } from '../api';
import CategoryModal from './categories/CategoryModal';
import { confirmAdminAction } from '../utils/adminConfirm';

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const data = await apiGetAllCategories();
      setCategories(
        [...(data.categories || [])].sort((first, second) => Number(first.id) - Number(second.id))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    const confirmed = await confirmAdminAction(
      'Books assigned to this category may be affected.',
      { title: 'Delete category', confirmLabel: 'Delete' }
    );
    if (!confirmed) return;
    try {
      await apiDeleteCategory(id);
      setCategories(categories.filter((c) => c.id !== id));
    } catch { /* API client displays the error toast. */ }
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditing(null);
    load();
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-black mt-4">Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-black">{categories.length} categories</p>
        <button
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition"
        >
          <FiPlus size={16} /> Add Category
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-black">
            <tr>
              <th className="px-4 py-3 text-left">ID</th>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Slug</th>
              <th className="px-4 py-3 text-left">Icon</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {categories.map((cat) => (
              <tr key={cat.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-black">#{cat.id}</td>
                <td className="px-4 py-3 font-medium text-black">{cat.name}</td>
                <td className="px-4 py-3 text-black font-mono text-xs">{cat.slug}</td>
                <td className="px-4 py-3 text-black">{cat.icon || '-'}</td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      cat.isActive !== false
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-black'
                    }`}
                  >
                    {cat.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button
                    onClick={() => {
                      setEditing(cat);
                      setShowModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(cat.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {categories.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-black">
                  No categories found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <CategoryModal
          category={editing}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default CategoriesManager;
